import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, HeartHandshake } from "lucide-react";
import { PageHero, SectionHead, Reveal, GoldButton } from "@/components/bits/Reveal";
import { IMAGES, PILLARS, VALUES, STATS } from "@/data/content";
import { Counter } from "@/components/bits/Reveal";
import { useSEO } from "@/lib/seo";

export default function About() {
  useSEO(
    "About Us | Maqline Solutions Pvt Ltd — Workforce Solutions Company, Bengaluru",
    "About Maqline Solutions Pvt Ltd — a Bengaluru-based manpower outsourcing and workforce solutions company connecting businesses with dependable talent and students with career opportunities across India."
  );

  return (
    <main data-testid="about-page">
      <PageHero
        overline="About Maqline Solutions"
        titleLines={["Connecting Talent,", "Industry and Opportunity"]}
        description="A workforce solutions company built around one belief — strong businesses are built by dependable people, and dependable careers are built by real opportunity."
        image={IMAGES.factory}
      />

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <SectionHead overline="Our Story" title="Why Maqline exists" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-5 text-muted leading-relaxed text-base">
                <p>
                  Maqline Solutions Pvt Ltd was established in 2026 in Bengaluru — a city where world-class
                  manufacturing meets one of India's deepest pools of young technical talent. We saw a gap:
                  organizations struggling to find dependable manpower, and capable students and job seekers
                  struggling to find a genuine doorway into industry.
                </p>
                <p>
                  We exist to close that gap. As a growing workforce-solutions organization, our ambition is
                  long-term: to become the most trusted bridge between industrial employers and the people
                  who power them.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-px bg-ink/10 border border-ink/10 self-start">
            {[
              { t: "Who We Are", d: "A workforce solutions company focused on bridging the gap between organizational manpower requirements and available talent." },
              { t: "What We Do", d: "We support businesses with workforce sourcing, recruitment, staffing and deployment — while helping students and job seekers access internships, industrial training and employment opportunities." },
              { t: "Our Approach", d: "Candidate sourcing, structured screening, workforce coordination and client-focused support — combined to help organizations build dependable teams." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08} className="bg-white">
                <div className="p-7">
                  <h3 className="font-display text-xl text-navy mb-2">{b.t}</h3>
                  <p className="text-sm text-muted leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-darknavy noise-bg py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="border border-gold/25 rounded-sm p-9 bg-white/[0.03]">
              <Target className="text-lightgold mb-5" size={28} />
              <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-lightgold mb-4">Mission</p>
              <p className="font-display text-2xl sm:text-3xl text-white leading-snug">
                To connect organizations with capable talent while creating meaningful employment, training
                and career opportunities for individuals.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-white/12 rounded-sm p-9 bg-white/[0.03]">
              <Eye className="text-lightgold mb-5" size={28} />
              <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-lightgold mb-4">Vision</p>
              <p className="font-display text-2xl sm:text-3xl text-white leading-snug">
                To become a trusted workforce solutions partner for leading organizations across India.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-steel py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="How We Work" title={["Four pillars,", "one discipline."]} />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="bg-white border border-ink/8 hover:border-gold/50 rounded-sm p-7 h-full transition-colors duration-400">
                  <span className="font-mono text-3xl text-gold/50 font-bold">{p.num}</span>
                  <h3 className="font-display text-xl text-ink mt-4 mb-2">{p.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Core Values" title="What we hold ourselves to" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.07} className="bg-white">
                <div className="p-8 h-full hover:bg-steel transition-colors duration-400">
                  <HeartHandshake size={20} className="text-gold mb-4" />
                  <h3 className="font-display text-xl text-ink mb-2">{v.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy noise-bg py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <SectionHead dark overline="Our Growth" title={["Young company.", "Serious momentum."]} description="Established in 2026, Maqline is a growing workforce-solutions organization with an ambitious long-term vision — and a track record that is already compounding." />
              <Reveal delay={0.2}>
                <div className="mt-8 space-y-4 text-white/60 text-sm leading-relaxed">
                  <p><span className="text-lightgold font-semibold">Who we serve:</span> manufacturing companies, MNCs and growing organizations — and the students, freshers and job seekers who power them.</p>
                  <p><span className="text-lightgold font-semibold">Our commitment:</span> honest representation, structured process and dependable coordination on every engagement, whether it is one hire or one hundred.</p>
                </div>
              </Reveal>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {STATS.slice(0, 4).map((s) => (
                <div key={s.label} className="bg-navy p-7">
                  <p className="font-mono text-4xl font-bold text-lightgold">{s.raw ? s.value : <Counter value={s.value} suffix={s.suffix} />}</p>
                  <p className="mt-2 text-xs text-white/60 leading-relaxed">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="font-quote italic text-xl text-white/80">"Connecting Talent With Opportunity."</p>
              <Link to="/contact" data-testid="about-cta"><GoldButton gold>Start a Conversation <ArrowRight size={15} /></GoldButton></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
